import React, { useState, useEffect, useCallback, memo } from 'react';
import { Modal, Form, Input, Select, DatePicker, Button, Spin, Table, Typography, Tag, Space, notification, Divider, Dropdown, Radio, Card } from 'antd';
import { SearchOutlined, PrinterOutlined, FileWordOutlined, FilePdfOutlined, FileExcelOutlined, EyeOutlined, SettingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import api from '../../utils/api';
import moment from 'moment';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ReportTemplateConfig from './ReportTemplateConfig';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const GlobalSearchModal = ({ visible, onClose }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [allResults, setAllResults] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [lookupData, setLookupData] = useState({
        ticketStatuses: [],
        ticketTypes: [],
        ticketCategories: [],
        priorities: [],
        products: [],
        productCategories: [],
        contracts: [],
        users: [],
        organizations: [],
    });
    const [exportTemplate, setExportTemplate] = useState('detailed');
    const [showTemplateSelector, setShowTemplateSelector] = useState(false);
    const [showTemplateConfig, setShowTemplateConfig] = useState(false);
    const [templateConfig, setTemplateConfig] = useState({
        templateType: 'detailed',
        includeLogo: true,
        includeSummary: true,
        includeFooter: true,
        includeCharts: false,
        companyName: 'Our Company',
        reportTitle: 'Ticket Analysis Report',
        colorScheme: 'blue',
        logo: null,
        customColors: {
            primary: '#1890ff',
            secondary: '#f0f0f0',
            text: '#000000'
        },
        pageOrientation: 'landscape'
    });

    const fetchLookupData = useCallback(async () => {
        setLoading(true);
        try {
            const requests = [
                api.get('/api/lookups/ticket-statuses').catch(e => ({ data: { data: [] }})),
                api.get('/api/lookups/ticket-types').catch(e => ({ data: { data: [] }})),
                api.get('/api/lookups/ticket-categories').catch(e => ({ data: { data: [] }})),
                api.get('/api/lookups/priorities').catch(e => ({ data: { data: [] }})),
                api.get('/api/lookups/products').catch(e => ({ data: { data: [] }})),
                api.get('/api/lookups/product-categories').catch(e => ({ data: { data: [] }})),
                api.get('/api/lookups/contracts').catch(e => ({ data: { data: [] }})),
                api.get('/api/users?limit=1000').catch(e => ({ data: { data: [] }})),
                api.get('/api/organizations').catch(e => ({ data: { data: [] }}))
            ];

            const results = await Promise.all(requests);
            
            setLookupData({
                ticketStatuses: results[0].data?.data || [],
                ticketTypes: results[1].data?.data || [],
                ticketCategories: results[2].data?.data || [],
                priorities: results[3].data?.data || [],
                products: results[4].data?.data || [],
                productCategories: results[5].data?.data || [],
                contracts: results[6].data?.data || [],
                users: results[7].data?.data || [],
                organizations: results[8].data?.data || []
            });
        } catch (error) {
            console.error('Error fetching lookup data:', error);
            notification.error({
                message: 'Error loading filters',
                description: 'Failed to load some filter options.'
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (visible) {
            fetchLookupData();
            form.resetFields();
            setSearchResults([]);
            setAllResults([]);
            setPagination({ current: 1, pageSize: 10, total: 0 });
            form.setFieldsValue({ sortBy: 'created_at:DESC' });
        }
    }, [visible, fetchLookupData, form]);

    const handleSearch = async (values, page = 1, pageSize = pagination.pageSize) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pageSize,
                search: values.generalSearch,
                statusId: values.statusId && values.statusId.join(','),
                priorityId: values.priorityId && values.priorityId.join(','),
                ticketTypeId: values.ticketTypeId && values.ticketTypeId.join(','),
                productCategoryId: values.productCategoryId && values.productCategoryId.join(','),
                productId: values.productId && values.productId.join(','),
                contractId: values.contractId && values.contractId.join(','),
                ticketCategoryId: values.ticketCategoryId && values.ticketCategoryId.join(','),
                userId: values.userId,
                organizationId: values.organizationId && values.organizationId.join(','),
                createdDateFrom: values.createdDateRange?.[0]?.format('YYYY-MM-DD'),
                createdDateTo: values.createdDateRange?.[1]?.format('YYYY-MM-DD'),
                closedDateFrom: values.closedDateRange?.[0]?.format('YYYY-MM-DD'),
                closedDateTo: values.closedDateRange?.[1]?.format('YYYY-MM-DD'),
                sortBy: values.sortBy || 'created_at:DESC',
            };

            Object.keys(params).forEach(key => {
                if (params[key] === undefined || params[key] === '') {
                    delete params[key];
                }
            });

            const response = await api.get('/api/lookups/global-ticket-search', { params });
            setSearchResults(response.data.data || []);
            setAllResults(response.data.data || []);
            setPagination({
                current: response.data.pagination.page,
                pageSize: response.data.pagination.limit,
                total: response.data.pagination.total,
            });
        } catch (error) {
            console.error('Error during global ticket search:', error);
            notification.error({
                message: 'Search Error',
                description: error.response?.data?.message || 'Failed to perform search.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));
        handleSearch(form.getFieldsValue(), newPagination.current, newPagination.pageSize);
    };

    const handleClearFilters = () => {
        form.resetFields();
        form.setFieldsValue({ sortBy: 'created_at:DESC' });
        setSearchResults([]);
        setAllResults([]);
        setPagination({ current: 1, pageSize: 10, total: 0 });
    };

    const fetchAllFilteredData = async () => {
        try {
            const values = form.getFieldsValue();
            const params = {
                limit: 10000,
                page: 1,
                search: values.generalSearch,
                statusId: values.statusId && values.statusId.join(','),
                priorityId: values.priorityId && values.priorityId.join(','),
                ticketTypeId: values.ticketTypeId && values.ticketTypeId.join(','),
                productCategoryId: values.productCategoryId && values.productCategoryId.join(','),
                productId: values.productId && values.productId.join(','),
                contractId: values.contractId && values.contractId.join(','),
                ticketCategoryId: values.ticketCategoryId && values.ticketCategoryId.join(','),
                userId: values.userId,
                organizationId: values.organizationId && values.organizationId.join(','),
                createdDateFrom: values.createdDateRange?.[0]?.format('YYYY-MM-DD'),
                createdDateTo: values.createdDateRange?.[1]?.format('YYYY-MM-DD'),
                closedDateFrom: values.closedDateRange?.[0]?.format('YYYY-MM-DD'),
                closedDateTo: values.closedDateRange?.[1]?.format('YYYY-MM-DD'),
                sortBy: values.sortBy || 'created_at:DESC',
            };

            Object.keys(params).forEach(key => {
                if (params[key] === undefined || params[key] === '') {
                    delete params[key];
                }
            });

            const response = await api.get('/api/lookups/global-ticket-search', { params });
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching filtered data:', error);
            notification.error({
                message: 'Export Error',
                description: 'Failed to fetch data for export.'
            });
            return [];
        }
    };

    const generateReportTitle = () => {
        const filters = form.getFieldsValue();
        let title = templateConfig.reportTitle;
        
        if (filters.generalSearch) {
            title += ` - Search: "${filters.generalSearch}"`;
        }
        if (filters.createdDateRange?.[0]) {
            title += ` - From: ${filters.createdDateRange[0].format('MMM D, YYYY')}`;
        }
        if (filters.createdDateRange?.[1]) {
            title += ` To: ${filters.createdDateRange[1].format('MMM D, YYYY')}`;
        }
        
        return title;
    };

    const getColorScheme = () => {
        if (templateConfig.colorScheme === 'custom') {
            return {
                primary: hexToRgb(templateConfig.customColors.primary),
                secondary: hexToRgb(templateConfig.customColors.secondary),
                text: hexToRgb(templateConfig.customColors.text)
            };
        }
        const schemes = {
            blue: { primary: [66, 139, 202], secondary: [240, 240, 240], text: [0, 0, 0] },
            green: { primary: [76, 175, 80], secondary: [232, 245, 233], text: [0, 0, 0] },
            purple: { primary: [103, 58, 183], secondary: [243, 229, 245], text: [0, 0, 0] },
            orange: { primary: [255, 152, 0], secondary: [255, 243, 224], text: [0, 0, 0] }
        };
        return schemes[templateConfig.colorScheme] || schemes.blue;
    };

    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? [
            parseInt(result[1], 16),
            parseInt(result[2], 16),
            parseInt(result[3], 16)
        ] : [0, 0, 0];
    };

    const getLogoDimensions = (imgData, maxWidth, maxHeight) => {
        const img = new Image();
        img.src = imgData;
        const aspectRatio = img.width / img.height;
        let width = maxWidth;
        let height = maxWidth / aspectRatio;
        if (height > maxHeight) {
            height = maxHeight;
            width = maxHeight * aspectRatio;
        }
        return { width, height };
    };

    const generateCoverPage = (pdf, colors) => {
        pdf.setFillColor(...colors.primary);
        pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), 'F');
        
        pdf.setTextColor(255, 255, 255);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(24);
        pdf.text(templateConfig.companyName, pdf.internal.pageSize.getWidth() / 2, 80, { align: 'center' });
        
        pdf.setFontSize(18);
        pdf.text(generateReportTitle(), pdf.internal.pageSize.getWidth() / 2, 100, { align: 'center' });
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Generated on: ${moment().format('MMMM D, YYYY h:mm A')}`, pdf.internal.pageSize.getWidth() / 2, 120, { align: 'center' });
        
        if (templateConfig.includeLogo && templateConfig.logo) {
            try {
                const { width, height } = getLogoDimensions(templateConfig.logo, 50, 15);
                pdf.addImage(templateConfig.logo, 'PNG', pdf.internal.pageSize.getWidth() / 2 - width / 2, 140, width, height);
            } catch (error) {
                console.error('Error adding logo to cover page:', error);
            }
        }

        pdf.addPage();
    };

    const generateReportHeader = (pdf, tickets) => {
        const colors = getColorScheme();
        const pageWidth = pdf.internal.pageSize.getWidth();
        let startY = 10;

        pdf.setFillColor(...colors.primary);
        pdf.rect(0, startY, pageWidth, 30, 'F');

        // Logo on top left
        if (templateConfig.includeLogo && templateConfig.logo) {
            try {
                const { width, height } = getLogoDimensions(templateConfig.logo, 40, 12);
                pdf.addImage(templateConfig.logo, 'PNG', 10, startY + 5, width, height);
            } catch (error) {
                console.error('Error adding logo to header:', error);
            }
        }

        // Title in center
        pdf.setTextColor(255, 255, 255);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text(generateReportTitle(), pageWidth / 2, startY + 18, { align: 'center' });

        // Generation info on right
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.text(`Generated: ${moment().format('MMM D, YYYY h:mm A')}`, pageWidth - 10, startY + 10, { align: 'right' });
        pdf.text(`Total Records: ${tickets.length}`, pageWidth - 10, startY + 17, { align: 'right' });
        
        return startY + 35;
    };

    const getReporterName = (ticket) => {
        const name = [ticket.creator_first_name, ticket.creator_last_name].filter(Boolean).join(' ').trim();
        return name || ticket.creator_email || ticket.created_by || 'Unknown';
    };

    const exportToPDF = async () => {
        setExportLoading(true);
        try {
            const tickets = await fetchAllFilteredData();
            if (tickets.length === 0) {
                notification.warning({ message: 'No data to export' });
                return;
            }

            const isLandscape = templateConfig.pageOrientation === 'landscape';
            const pdf = new jsPDF({
                orientation: templateConfig.pageOrientation,
                unit: 'mm',
                format: 'a4'
            });
            const colors = getColorScheme();

            // Add cover page
            generateCoverPage(pdf, colors);

            let startY = generateReportHeader(pdf, tickets);
            
            // Add summary section if enabled
            if (templateConfig.includeSummary) {
                pdf.setFontSize(12);
                pdf.setTextColor(...colors.text);
                pdf.setFont('helvetica', 'bold');
                pdf.text('Executive Summary', 10, startY);
                startY += 10;

                const summaryData = {
                    totalTickets: tickets.length,
                    avgAge: Math.round(tickets.reduce((sum, ticket) => sum + moment().diff(moment(ticket.created_at), 'days'), 0) / tickets.length),
                    openTickets: tickets.filter(t => t.status_name.toLowerCase() === 'open').length,
                    closedTickets: tickets.filter(t => t.status_name.toLowerCase() === 'closed').length
                };

                autoTable(pdf, {
                    startY,
                    head: [['Metric', 'Value']],
                    body: [
                        ['Total Tickets', summaryData.totalTickets],
                        ['Average Ticket Age (days)', summaryData.avgAge],
                        ['Open Tickets', summaryData.openTickets],
                        ['Closed Tickets', summaryData.closedTickets]
                    ],
                    theme: 'grid',
                    headStyles: {
                        fillColor: colors.primary,
                        textColor: 255,
                        fontStyle: 'bold',
                        fontSize: 10
                    },
                    bodyStyles: {
                        fontSize: 9,
                        textColor: colors.text,
                        fillColor: colors.secondary
                    },
                    margin: { left: 10, right: 10 }
                });
                startY = pdf.lastAutoTable.finalY + 10;
            }

            const headers = [];
            const data = [];

            if (templateConfig.templateType === 'summary') {
                headers.push(['Status', 'Priority', 'Count', 'Avg Age (days)', 'Oldest', 'Newest']);
                const summaryData = {};
                tickets.forEach(ticket => {
                    const key = `${ticket.status_name}-${ticket.priority_name}`;
                    if (!summaryData[key]) {
                        summaryData[key] = {
                            status: ticket.status_name,
                            priority: ticket.priority_name,
                            count: 0,
                            totalAge: 0,
                            oldest: moment(ticket.created_at),
                            newest: moment(ticket.created_at)
                        };
                    }
                    summaryData[key].count++;
                    const age = moment().diff(moment(ticket.created_at), 'days');
                    summaryData[key].totalAge += age;
                    if (moment(ticket.created_at).isBefore(summaryData[key].oldest)) {
                        summaryData[key].oldest = moment(ticket.created_at);
                    }
                    if (moment(ticket.created_at).isAfter(summaryData[key].newest)) {
                        summaryData[key].newest = moment(ticket.created_at);
                    }
                });

                Object.values(summaryData).forEach(item => {
                    data.push([
                        item.status,
                        item.priority,
                        item.count.toString(),
                        Math.round(item.totalAge / item.count).toString(),
                        item.oldest.format('MMM D'),
                        item.newest.format('MMM D')
                    ]);
                });
            } else {
                headers.push(['Ticket ID', 'Summary', 'Status', 'Priority', 'Reporter', 'Assignee', 'Created', 'Age (days)']);
                tickets.forEach(ticket => {
                    const created = moment(ticket.created_at);
                    const age = Math.floor(moment().diff(created, 'days', true));
                    data.push([
                        ticket.ticket_uuid,
                        ticket.title || 'N/A',
                        ticket.status_name || 'N/A',
                        ticket.priority_name || 'N/A',
                        getReporterName(ticket),
                        ticket.assigned_to_user_id ? (ticket.assignee_first_name ? `${ticket.assignee_first_name} ${ticket.assignee_last_name}`.substring(0, 18) : ticket.assignee_email || 'Assigned') : 'Unassigned',
                        created.format('MMM D, YY'),
                        age.toString()
                    ]);
                });
            }

            // Adjust column widths based on orientation
            const columnStyles = isLandscape ? {
                0: { cellWidth: 40 }, // Ticket ID
                1: { cellWidth: 50 }, // Summary
                2: { cellWidth: 30 }, // Status
                3: { cellWidth: 30 }, // Priority
                4: { cellWidth: 40 }, // Reporter
                5: { cellWidth: 40 }, // Assignee
                6: { cellWidth: 30 }, // Created
                7: { cellWidth: 20 }  // Age
            } : {
                0: { cellWidth: 25 }, // Ticket ID
                1: { cellWidth: 30 }, // Summary
                2: { cellWidth: 20 }, // Status
                3: { cellWidth: 20 }, // Priority
                4: { cellWidth: 25 }, // Reporter
                5: { cellWidth: 25 }, // Assignee
                6: { cellWidth: 20 }, // Created
                7: { cellWidth: 15 }  // Age
            };

            autoTable(pdf, {
                head: headers,
                body: data,
                startY,
                theme: 'grid',
                headStyles: {
                    fillColor: colors.primary,
                    textColor: 255,
                    fontStyle: 'bold',
                    fontSize: 10
                },
                bodyStyles: {
                    fontSize: 8,
                    cellPadding: 2,
                    textColor: colors.text,
                    overflow: 'linebreak',
                    minCellHeight: 5,
                    valign: 'middle'
                },
                alternateRowStyles: {
                    fillColor: colors.secondary
                },
                margin: { left: 10, right: 10 },
                columnStyles,
                didParseCell: (data) => {
                    if (data.column.index === 0 && data.cell.section === 'body') {
                        data.cell.styles.fontSize = 7; // Smaller font for Ticket ID
                    }
                    if (data.column.index === 1 && data.cell.section === 'body') {
                        data.cell.styles.lineWidth = 0.1; // Thinner lines for Summary
                    }
                }
            });

            // Add footer if included
            if (templateConfig.includeFooter) {
                const pageCount = pdf.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    pdf.setPage(i);
                    pdf.setFontSize(8);
                    pdf.setTextColor(...colors.text);
                    pdf.text(
                        `Page ${i} of ${pageCount} - ${templateConfig.companyName}`,
                        pdf.internal.pageSize.getWidth() / 2,
                        pdf.internal.pageSize.getHeight() - 10,
                        { align: 'center' }
                    );
                }
            }

            pdf.save(`ticket-report-${moment().format('YYYY-MM-DD')}.pdf`);
            
            notification.success({
                message: 'PDF Export Complete',
                description: `Exported ${tickets.length} tickets with professional formatting.`
            });
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            notification.error({
                message: 'Export Failed',
                description: 'Failed to generate PDF report.'
            });
        } finally {
            setExportLoading(false);
        }
    };

const exportToWord = async () => {
    setExportLoading(true);
    try {
        const tickets = await fetchAllFilteredData();
        if (tickets.length === 0) {
            notification.warning({ message: 'No data to export' });
            return;
        }

        const colors = getColorScheme();
        const primaryColor = `rgb(${colors.primary.join(',')})`;
        const secondaryColor = `rgb(${colors.secondary.join(',')})`;
        const textColor = `rgb(${colors.text.join(',')})`;
        const isLandscape = templateConfig.pageOrientation === 'landscape';

        // Create a table-based layout for landscape orientation that Word can understand
        const landscapeStyle = isLandscape ? `
        <style>
            /* Word-compatible landscape layout using tables */
            .landscape-wrapper {
                width: 100%;
                height: 100%;
            }
            .landscape-table {
                width: 100%;
                border-collapse: collapse;
            }
            .landscape-cell {
                padding: 0;
                vertical-align: top;
            }
        </style>
        <div class="landscape-wrapper">
        <table class="landscape-table"><tr><td class="landscape-cell">` : '';

        const landscapeEnd = isLandscape ? `</td></tr></table></div>` : '';

        const htmlContent = `
<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" 
      xmlns:w="urn:schemas-microsoft-com:office:word" 
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
    <meta charset="UTF-8">
    <meta name="ProgId" content="Word.Document">
    <meta name="Generator" content="Microsoft Word 15">
    <meta name="Originator" content="Microsoft Word 15">
    <title>${generateReportTitle()}</title>
    <style>
        /* Word-specific styles */
        @page {
            size: ${isLandscape ? 'landscape' : 'portrait'};
            margin: 1in;
            mso-page-orientation: ${isLandscape ? 'landscape' : 'portrait'};
        }
        body {
            font-family: 'Arial', sans-serif;
            margin: 0;
            padding: 0;
            color: ${textColor};
            line-height: 1.2;
        }
        .cover-page {
            text-align: center;
            padding: 1in 0;
            page-break-after: always;
        }
        .cover-page h1 {
            font-size: 24pt;
            color: ${primaryColor};
            margin-bottom: 20pt;
            font-weight: bold;
        }
        .cover-page h2 {
            font-size: 18pt;
            color: ${textColor};
            margin-bottom: 15pt;
        }
        .cover-page p {
            font-size: 12pt;
            color: ${textColor};
        }
        .header {
            background: ${primaryColor};
            color: white;
            padding: 15pt;
            margin-bottom: 20pt;
            display: table;
            width: 100%;
        }
        .header-content {
            display: table-row;
        }
        .header-logo, .header-title, .header-info {
            display: table-cell;
            vertical-align: middle;
        }
        .header-title {
            text-align: center;
            padding: 0 20pt;
        }
        .header-info {
            text-align: right;
        }
        .company-name {
            font-size: 16pt;
            font-weight: bold;
            margin: 0;
        }
        .report-title {
            font-size: 14pt;
            margin: 0;
            font-weight: bold;
        }
        .report-info {
            font-size: 10pt;
        }
        .logo {
            max-width: 120pt;
            max-height: 36pt;
        }
        .summary {
            background: ${secondaryColor};
            padding: 15pt;
            margin-bottom: 15pt;
            border-left: 4pt solid ${primaryColor};
        }
        .summary h3 {
            color: ${primaryColor};
            margin-top: 0;
            margin-bottom: 10pt;
            font-size: 14pt;
            font-weight: bold;
        }
        .summary-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11pt;
        }
        .summary-table th, .summary-table td {
            padding: 6pt;
            border: 1pt solid ${primaryColor};
            text-align: left;
        }
        .summary-table th {
            background: ${primaryColor};
            color: white;
            font-weight: bold;
        }
        .summary-table td {
            background: ${secondaryColor};
        }
        .data-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15pt;
            font-size: 10pt;
            table-layout: fixed;
        }
        .data-table th {
            background: ${primaryColor};
            color: white;
            padding: 8pt;
            text-align: left;
            font-weight: bold;
            border: 1pt solid ${primaryColor};
        }
        .data-table td {
            padding: 6pt;
            border: 1pt solid #ddd;
            color: ${textColor};
            word-wrap: break-word;
        }
        .data-table tr:nth-child(even) {
            background: ${secondaryColor};
        }
        .status {
            padding: 2pt 6pt;
            border-radius: 8pt;
            font-size: 9pt;
            font-weight: 600;
            text-align: center;
            display: inline-block;
        }
        .status-open { background: #e8f5e8; color: #2e7d32; }
        .status-pending { background: #fff3cd; color: #856404; }
        .status-closed { background: #f8d7da; color: #721c24; }
        .footer {
            margin-top: 30pt;
            padding-top: 15pt;
            border-top: 1pt solid #eee;
            text-align: center;
            color: ${textColor};
            font-size: 10pt;
        }
        
        /* Column widths for landscape vs portrait */
        .col-ticket-id { width: ${isLandscape ? '15%' : '12%'}; }
        .col-summary { width: ${isLandscape ? '22%' : '18%'}; }
        .col-status { width: ${isLandscape ? '10%' : '8%'}; }
        .col-priority { width: ${isLandscape ? '10%' : '8%'}; }
        .col-reporter { width: ${isLandscape ? '15%' : '14%'}; }
        .col-assignee { width: ${isLandscape ? '15%' : '14%'}; }
        .col-created { width: ${isLandscape ? '8%' : '10%'}; }
        .col-age { width: ${isLandscape ? '5%' : '6%'}; }
    </style>
    ${landscapeStyle}
</head>
<body>
    ${landscapeStyle ? '' : '<div>'} <!-- Start wrapper if not landscape -->
    
    <div class="cover-page">
        ${templateConfig.includeLogo && templateConfig.logo ? `<img src="${templateConfig.logo}" class="logo" alt="Company Logo" />` : ''}
        <h1>${templateConfig.companyName}</h1>
        <h2>${generateReportTitle()}</h2>
        <p>Generated on: ${moment().format('MMMM D, YYYY h:mm A')}</p>
    </div>
    
    <div class="header">
        <div class="header-content">
            ${templateConfig.includeLogo && templateConfig.logo ? `
            <div class="header-logo">
                <img src="${templateConfig.logo}" class="logo" alt="Company Logo" />
            </div>
            ` : '<div class="header-logo"></div>'}
            <div class="header-title">
                <div class="report-title">${generateReportTitle()}</div>
            </div>
            <div class="header-info">
                <div class="report-info">
                    Generated: ${moment().format('MMMM D, YYYY h:mm A')}<br>
                    Total Records: ${tickets.length}
                </div>
            </div>
        </div>
    </div>
    
    ${templateConfig.includeSummary ? `
    <div class="summary">
        <h3>Executive Summary</h3>
        <table class="summary-table">
            <thead>
                <tr>
                    <th>Metric</th>
                    <th>Value</th>
                </tr>
            </thead>
            <tbody>
                <tr><td>Total Tickets</td><td>${tickets.length}</td></tr>
                <tr><td>Average Ticket Age (days)</td><td>${Math.round(tickets.reduce((sum, ticket) => sum + moment().diff(moment(ticket.created_at), 'days'), 0) / tickets.length)}</td></tr>
                <tr><td>Open Tickets</td><td>${tickets.filter(t => t.status_name.toLowerCase() === 'open').length}</td></tr>
                <tr><td>Closed Tickets</td><td>${tickets.filter(t => t.status_name.toLowerCase() === 'closed').length}</td></tr>
            </tbody>
        </table>
    </div>
    ` : ''}

    <table class="data-table">
        <thead>
            <tr>
                <th class="col-ticket-id">Ticket ID</th>
                <th class="col-summary">Summary</th>
                <th class="col-status">Status</th>
                <th class="col-priority">Priority</th>
                <th class="col-reporter">Reporter</th>
                <th class="col-assignee">Assignee</th>
                <th class="col-created">Created Date</th>
                <th class="col-age">Age (days)</th>
            </tr>
        </thead>
        <tbody>
            ${tickets.map(ticket => {
                const created = moment(ticket.created_at);
                const age = Math.floor(moment().diff(created, 'days', true));
                return `
                <tr>
                    <td>${ticket.ticket_uuid}</td>
                    <td>${ticket.title || 'No title provided'}</td>
                    <td><span class="status status-${ticket.status_name?.toLowerCase()}">${ticket.status_name || 'Unknown'}</span></td>
                    <td>${ticket.priority_name || 'N/A'}</td>
                    <td>${getReporterName(ticket)}</td>
                    <td>${ticket.assigned_to_user_id ? (ticket.assignee_first_name ? `${ticket.assignee_first_name} ${ticket.assignee_last_name}` : ticket.assignee_email || 'Assigned') : 'Unassigned'}</td>
                    <td>${created.format('MMM D, YYYY')}</td>
                    <td>${age}</td>
                </tr>
                `;
            }).join('')}
        </tbody>
    </table>

    ${templateConfig.includeFooter ? `
    <div class="footer">
        <p>Confidential Report - ${templateConfig.companyName} | Generated by Helpdesk System v2.0 | ${moment().format('YYYY')}</p>
    </div>
    ` : ''}

    ${landscapeEnd}
</body>
</html>`;

        const blob = new Blob([htmlContent], { type: 'application/msword' });
        saveAs(blob, `ticket-report-${moment().format('YYYY-MM-DD')}.doc`);
        
        notification.success({
            message: 'Word Export Complete',
            description: `Exported ${tickets.length} tickets with professional formatting.`
        });
    } catch (error) {
        console.error('Error exporting to Word:', error);
        notification.error({
            message: 'Export Failed',
            description: 'Failed to generate Word document.'
        });
    } finally {
        setExportLoading(false);
    }
};

    const exportToCSV = async () => {
        setExportLoading(true);
        try {
            const tickets = await fetchAllFilteredData();
            if (tickets.length === 0) {
                notification.warning({ message: 'No data to export' });
                return;
            }

            const headers = [
                'Ticket UUID', 'Summary', 'Status', 'Priority', 
                'Reporter Name', 'Reporter Email', 'Assignee Name', 'Assignee Email',
                'Created Date', 'Closed Date', 'Ticket Type', 'Product', 'Contract',
                'Age (days)', 'Resolution Time (days)', 'Department', 'Category'
            ];
            
            let csvContent = headers.join(',') + '\n';
            
            tickets.forEach(ticket => {
                const created = moment(ticket.created_at);
                const closed = ticket.closed_at ? moment(ticket.closed_at) : null;
                const age = Math.floor(moment().diff(created, 'days', true));
                const resolutionTime = closed ? Math.floor(closed.diff(created, 'days', true)) : '';
                
                const row = [
                    `"${ticket.ticket_uuid}"`,
                    `"${(ticket.title || '').replace(/"/g, '""')}"`,
                    `"${ticket.status_name}"`,
                    `"${ticket.priority_name}"`,
                    `"${getReporterName(ticket)}"`,
                    `"${ticket.creator_email || ''}"`,
                    `"${ticket.assigned_to_user_id ? [ticket.assignee_first_name, ticket.assignee_last_name].filter(Boolean).join(' ') || ticket.assignee_email || 'Assigned' : 'Unassigned'}"`,
                    `"${ticket.assignee_email || ''}"`,
                    `"${created.format('YYYY-MM-DD HH:mm:ss')}"`,
                    `"${closed ? closed.format('YYYY-MM-DD HH:mm:ss') : ''}"`,
                    `"${ticket.ticket_type_name || ''}"`,
                    `"${ticket.product_name || ''}"`,
                    `"${ticket.contract_name || ''}"`,
                    age,
                    resolutionTime,
                    `"${ticket.department || ''}"`,
                    `"${ticket.category || ''}"`
                ];
                csvContent += row.join(',') + '\n';
            });

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, `ticket-report-${moment().format('YYYY-MM-DD')}.csv`);
            
            notification.success({
                message: 'CSV Export Complete',
                description: `Exported ${tickets.length} tickets with complete data.`
            });
        } catch (error) {
            console.error('Error exporting to CSV:', error);
            notification.error({
                message: 'Export Failed',
                description: 'Failed to generate CSV file.'
            });
        } finally {
            setExportLoading(false);
        }
    };

    const exportMenu = {
        items: [
            { key: 'template-selector', label: 'Choose Report Template', icon: <EyeOutlined />, onClick: () => setShowTemplateSelector(true) },
            { key: 'template-config', label: 'Configure Template', icon: <SettingOutlined />, onClick: () => setShowTemplateConfig(true) },
            { type: 'divider' },
            { key: 'word', label: 'Export to Word Document', icon: <FileWordOutlined />, onClick: exportToWord },
            { key: 'pdf', label: 'Export to PDF Report', icon: <FilePdfOutlined />, onClick: exportToPDF },
            { key: 'csv', label: 'Export to CSV Data', icon: <FileExcelOutlined />, onClick: exportToCSV }
        ]
    };

    const TemplateSelectorModal = () => (
        <Modal
            title="Choose Report Template"
            open={showTemplateSelector}
            onCancel={() => setShowTemplateSelector(false)}
            footer={[
                <Button key="cancel" onClick={() => setShowTemplateSelector(false)}>
                    Cancel
                </Button>,
                <Button key="config" onClick={() => {
                    setShowTemplateSelector(false);
                    setShowTemplateConfig(true);
                }}>
                    Customize Template
                </Button>,
                <Button key="export" type="primary" onClick={() => {
                    setShowTemplateSelector(false);
                    exportToPDF();
                }}>
                    Export with Selected Template
                </Button>
            ]}
        >
            <Radio.Group 
                value={exportTemplate} 
                onChange={(e) => {
                    setExportTemplate(e.target.value);
                    setTemplateConfig(prev => ({ ...prev, templateType: e.target.value }));
                }}
                style={{ width: '100%' }}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Card size="small" style={{ border: exportTemplate === 'detailed' ? '2px solid #1890ff' : '1px solid #d9d9d9' }}>
                        <Radio value="detailed">
                            <strong>Detailed Report</strong>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                Complete ticket listing with all details and analytics
                            </div>
                        </Radio>
                    </Card>
                    <Card size="small" style={{ border: exportTemplate === 'summary' ? '2px solid #1890ff' : '1px solid #d9d9d9' }}>
                        <Radio value="summary">
                            <strong>Executive Summary</strong>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                High-level overview with KPIs and performance metrics
                            </div>
                        </Radio>
                    </Card>
                    <Card size="small" style={{ border: exportTemplate === 'analytical' ? '2px solid #1890ff' : '1px solid #d9d9d9' }}>
                        <Radio value="analytical">
                            <strong>Analytical Dashboard</strong>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                Statistical analysis with charts and trends
                            </div>
                        </Radio>
                    </Card>
                </Space>
            </Radio.Group>
        </Modal>
    );

    const columns = [
        {
            title: 'Ticket UUID',
            dataIndex: 'ticket_uuid',
            key: 'ticket_uuid',
            width: 150,
            render: (text, record) => <a href={`/tickets/${record.id}`}>{text}</a>,
        },
        {
            title: 'Summary',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
            width: 250,
        },
        {
            title: 'Status',
            dataIndex: 'status_name',
            key: 'status_name',
            width: 100,
            render: (status) => <Tag color="blue">{status}</Tag>,
        },
        {
            title: 'Priority',
            dataIndex: 'priority_name',
            key: 'priority_name',
            width: 100,
            render: (priority) => <Tag color="orange">{priority}</Tag>,
        },
        {
            title: 'Reporter',
            key: 'creator_name',
            width: 150,
            render: (_, record) => getReporterName(record)
        },
        {
            title: 'Assignee',
            key: 'assignee_name',
            width: 150,
            render: (_, record) => {
                if (!record.assigned_to_user_id) return 'Unassigned';
                const name = [record.assignee_first_name, record.assignee_last_name]
                    .filter(Boolean).join(' ').trim();
                return name 
                    ? `${name} (${record.assignee_email || 'no email'})`
                    : record.assignee_email || 'Unknown';
            }
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (date) => moment(date).format('YYYY-MM-DD HH:mm'),
        },
        {
            title: 'Closed At',
            dataIndex: 'closed_at',
            key: 'closed_at',
            width: 180,
            render: (date) => date ? moment(date).format('YYYY-MM-DD HH:mm') : 'N/A',
        },
    ];

    return (
        <>
            <Modal
                title={
                    <Space>
                        <SearchOutlined />
                        Global Ticket Search & Professional Reporting
                    </Space>
                }
                open={visible}
                onCancel={onClose}
                footer={null}
                width={1200}
                style={{ top: 50 }}
                destroyOnClose={true}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={(values) => handleSearch(values, 1, pagination.pageSize)}
                    initialValues={{ sortBy: 'created_at:DESC' }}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                        <Form.Item name="generalSearch" label="Keywords">
                            <Input placeholder="Ticket UUID, title, description, user name..." allowClear />
                        </Form.Item>

                        <Form.Item name="statusId" label="Status">
                            <Select mode="multiple" placeholder="Select status(es)" allowClear>
                                {lookupData.ticketStatuses.map(status => (
                                    <Option key={status.id} value={status.id}>{status.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="priorityId" label="Priority">
                            <Select 
                                mode="multiple" 
                                placeholder="Select priority/priorities" 
                                allowClear
                                loading={loading}
                                notFoundContent={loading ? <Spin size="small" /> : "No priorities found"}
                            >
                                {lookupData.priorities.map(priority => (
                                    <Option key={priority.id} value={priority.id}>{priority.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="ticketTypeId" label="Ticket Type">
                            <Select mode="multiple" placeholder="Select type(s)" allowClear>
                                {lookupData.ticketTypes.map(type => (
                                    <Option key={type.id} value={type.id}>{type.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="ticketCategoryId" label="Ticket Category">
                            <Select mode="multiple" placeholder="Select category(ies)" allowClear>
                                {lookupData.ticketCategories.map(category => (
                                    <Option key={category.id} value={category.id}>{category.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="productId" label="Product">
                            <Select mode="multiple" placeholder="Select product(s)" allowClear>
                                {lookupData.products.map(product => (
                                    <Option key={product.id} value={product.id}>{product.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="productCategoryId" label="Product Category">
                            <Select mode="multiple" placeholder="Select product category(ies)" allowClear>
                                {lookupData.productCategories.map(category => (
                                    <Option key={category.id} value={category.id}>{category.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="contractId" label="Contract">
                            <Select mode="multiple" placeholder="Select contract(s)" allowClear>
                                {lookupData.contracts.map(contract => (
                                    <Option key={contract.id} value={contract.id}>{contract.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                        
                        <Form.Item name="userId" label="Created By / Assigned To">
                            <Select 
                                showSearch 
                                placeholder="Select user" 
                                allowClear
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                    if (!option.children) return false;
                                    return option.children.toLowerCase().includes(input.toLowerCase());
                                }}
                            >
                                {lookupData.users.map(user => (
                                    <Option key={user.id} value={user.id}>
                                        {user.first_name && user.last_name 
                                            ? `${user.first_name} ${user.last_name} (${user.email})`
                                            : user.email || `User ${user.id}`}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="organizationId" label="Organization">
                            <Select mode="multiple" placeholder="Select organization(s)" allowClear>
                                {lookupData.organizations.map(org => (
                                    <Option key={org.id} value={org.id}>{org.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="createdDateRange" label="Created Date Range">
                            <RangePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>

                        <Form.Item name="closedDateRange" label="Closed Date Range">
                            <RangePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>

                        <Form.Item name="sortBy" label="Sort By">
                            <Select placeholder="Select sort option">
                                <Option value="created_at:DESC">Created At (Newest First)</Option>
                                <Option value="created_at:ASC">Created At (Oldest First)</Option>
                                <Option value="closed_at:DESC">Closed At (Newest First)</Option>
                                <Option value="closed_at:ASC">Closed At (Oldest First)</Option>
                                <Option value="title:ASC">Summary (A-Z)</Option>
                                <Option value="title:DESC">Summary (Z-A)</Option>
                                <Option value="ticket_uuid:ASC">Ticket UUID (Asc)</Option>
                                <Option value="ticket_uuid:DESC">Ticket UUID (Desc)</Option>
                                <Option value="status_name:ASC">Status (A-Z)</Option>
                                <Option value="status_name:DESC">Status (Z-A)</Option>
                                <Option value="priority_name:ASC">Priority (A-Z)</Option>
                                <Option value="priority_name:DESC">Priority (Z-A)</Option>
                            </Select>
                        </Form.Item>
                    </div>

                    <Form.Item style={{ textAlign: 'right', marginTop: '20px' }}>
                        <Space>
                            <Button onClick={handleClearFilters}>Clear Filters</Button>
                            <Dropdown menu={exportMenu} placement="bottomRight">
                                <Button type="primary" icon={<PrinterOutlined />} loading={exportLoading}>
                                    Professional Reports
                                </Button>
                            </Dropdown>
                            <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
                                Search
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>

                <Divider />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={5} style={{ margin: 0 }}>
                        Search Results ({pagination.total} tickets found)
                    </Title>
                    <Space>
                        <Text type="secondary">Current filters applied</Text>
                        <Dropdown menu={exportMenu} placement="bottomRight">
                            <Button type="primary" icon={<PrinterOutlined />} loading={exportLoading} size="small">
                                Export
                            </Button>
                        </Dropdown>
                    </Space>
                </div>

                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={searchResults}
                        rowKey="id"
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 'max-content' }}
                        locale={{ emptyText: 'No tickets found matching your criteria.' }}
                    />
                </Spin>
            </Modal>

            <TemplateSelectorModal />
            <ReportTemplateConfig
                visible={showTemplateConfig}
                onClose={() => setShowTemplateConfig(false)}
                onSave={setTemplateConfig}
                initialConfig={templateConfig}
            />
        </>
    );
};

GlobalSearchModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default memo(GlobalSearchModal);